import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  httpClient,
  NormalizedError,
  setIdentity,
} from "@/shared/api/httpClient";
import { decodeJwt } from "@/shared/auth/decodeJwt";
import { tokenStore } from "@/shared/auth/tokenStore";

const AuthContext = createContext(null);
const SUPPORTED_ROLE_ID = "end_merchant";
const UNSUPPORTED_ROLE_REASON = "unsupported_role";

function createUnsupportedRoleError() {
  return new NormalizedError({
    kind: "forbidden",
    status: 403,
    code: UNSUPPORTED_ROLE_REASON,
    message:
      "This account belongs to the admin dashboard. Please sign in at the control center.",
  });
}

function getTokenRoleId(token) {
  const claims = decodeJwt(token);
  return claims?.role_id || claims?.roleId || claims?.role || null;
}

function getUserRoleId(user, token) {
  return user?.role_id || user?.roleId || user?.role || getTokenRoleId(token);
}

function markUnsupportedRoleSignInReason() {
  try {
    sessionStorage.setItem("vexgate_signin_reason", UNSUPPORTED_ROLE_REASON);
  } catch {
    // ignore — privacy mode
  }
}

function markExpiredSessionSignInReason() {
  try {
    sessionStorage.setItem("vexgate_signin_reason", "expired");
  } catch {
    // ignore — privacy mode
  }
}

function assertSupportedUserRole(user, token) {
  if (getUserRoleId(user, token) !== SUPPORTED_ROLE_ID) {
    throw createUnsupportedRoleError();
  }
}

export function AuthProvider({ children }) {
  const queryClient = useQueryClient();
  const [sessionToken, setSessionToken] = useState(() => tokenStore.get());

  const loadSessionForToken = useCallback(async (token) => {
    const authHeaders = { Authorization: `Bearer ${token}` };
    const user = await httpClient.get("/user", {
      auth: "single-purpose",
      headers: authHeaders,
    });
    assertSupportedUserRole(user, token);
    const parentGroups = await httpClient.get("/user/role/v3", {
      auth: "single-purpose",
      headers: authHeaders,
    });
    return { user, parentGroups };
  }, []);

  const clearSessionLocal = useCallback(() => {
    tokenStore.clear();
    setIdentity(null);
    setSessionToken(null);
    queryClient.clear();
  }, [queryClient]);

  const acceptSessionToken = useCallback(
    async (token) => {
      try {
        const session = await loadSessionForToken(token);
        tokenStore.set(token);
        setSessionToken(token);
        // Update identity before any invalidate/refetch so X-Profile-Id matches
        // the new session immediately (render-time setIdentity would be too late).
        if (session.user?.merchant_id && session.user?.profile_id) {
          setIdentity({
            merchantId: session.user.merchant_id,
            profileId: session.user.profile_id,
          });
        }
        queryClient.setQueryData(["auth", "me"], session);
        return session;
      } catch (err) {
        clearSessionLocal();
        throw err;
      }
    },
    [clearSessionLocal, loadSessionForToken, queryClient],
  );

  const meQuery = useQuery({
    queryKey: ["auth", "me"],
    enabled: !!sessionToken,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    retry: false,
    queryFn: async () => {
      // Read from tokenStore (not closed-over sessionToken) so refetches after
      // acceptSessionToken / profile switch use the JWT that was just written,
      // even before React re-renders with the new sessionToken state.
      const token = tokenStore.get();
      if (!token) {
        throw new NormalizedError({
          kind: "unauthorized",
          status: 401,
          code: "missing_token",
          message: "No session token.",
        });
      }
      try {
        return await loadSessionForToken(token);
      } catch (err) {
        if (err?.code === UNSUPPORTED_ROLE_REASON) {
          markUnsupportedRoleSignInReason();
          clearSessionLocal();
        } else if (err?.kind === "unauthorized" || err?.status === 401) {
          markExpiredSessionSignInReason();
          clearSessionLocal();
        }
        throw err;
      }
    },
  });

  // 'error' is for non-401 bootstrap failures (5xx, network) where the token
  // is still likely valid. 401s clear the local session in the queryFn catch
  // above, which makes ProtectedRoute send the user back to /signin.
  const status = !sessionToken
    ? "unauthenticated"
    : meQuery.isPending || meQuery.isLoading
      ? "loading"
      : meQuery.isError
        ? "error"
        : "authenticated";

  const bootstrapError = meQuery.isError ? meQuery.error : null;
  const refetchMe = meQuery.refetch;

  const user = meQuery.data?.user ?? null;
  const parentGroups = meQuery.data?.parentGroups ?? [];

  // Register active merchant/profile with httpClient so profile-scoped routes
  // (ProfilePaymentRead, ProfileSubscriptionRead, etc.) receive X-Merchant-Id
  // + X-Profile-Id headers.
  //
  // Set synchronously DURING render rather than in a post-render useEffect
  // because children (SubscriptionsListPage, PayoutsListPage, etc.) fire
  // their useApiQuery on mount in the same React commit as this provider's
  // re-render-with-user — before any post-commit effect could run. A useEffect
  // here would race: first request goes out without identity headers and the
  // backend rejects with `Missing required param: X-Profile-Id` (subscription
  // route's `extract_profile_id` runs before auth middleware).
  //
  // setIdentity is idempotent (writes to a module-level var); calling it
  // multiple times during render is harmless. Cleanup on signout is handled
  // by signOut() / signOutLocal() below.
  if (user?.merchant_id && user?.profile_id) {
    setIdentity({ merchantId: user.merchant_id, profileId: user.profile_id });
  }

  const hasParent = useCallback(
    (parentName) => parentGroups.some((g) => g?.name === parentName),
    [parentGroups],
  );

  const hasPermission = useCallback(
    ({ resource, scope }) => {
      // /user/role/v3 returns resource/scope strings in lowercase, snake_case
      // for multi-word names (e.g. "refund", "write", "api_key", "webhook_event").
      // Spec convention uses PascalCase ('Refund', 'Write', 'ApiKey', 'WebhookEvent').
      // Bridge by case-folding AND stripping non-alphanumerics on both sides.
      const norm = (x) =>
        String(x || "")
          .toLowerCase()
          .replace(/[^a-z0-9]/g, "");
      const r = norm(resource);
      const s = norm(scope);
      return parentGroups.some(
        (g) =>
          Array.isArray(g?.resources) &&
          g.resources.some((x) => norm(x) === r) &&
          Array.isArray(g?.scopes) &&
          g.scopes.some((x) => norm(x) === s),
      );
    },
    [parentGroups],
  );

  const signIn = useCallback(
    async (email, password) => {
      const res = await httpClient.post(
        "/user/signin",
        { email, password },
        { auth: "none" },
      );
      // res = { token, token_type }
      if (res?.token_type === "user_info" && res?.token) {
        await acceptSessionToken(res.token);
      }
      return res;
    },
    [acceptSessionToken],
  );

  // Email-link entry: exchanges the EmailToken (from `?token=` in the merchant
  // URL the backend emails out) for a SinglePurposeToken via NoAuth endpoint.
  // Response = { token: SPT, token_type: <next required step> } per
  // NextFlow::from_origin in vexgate decision_manager.rs. Caller then
  // dispatches via routeForPurpose(token_type).
  const userFromEmail = useCallback(async (emailToken) => {
    return httpClient.post(
      "/user/from_email",
      { token: emailToken },
      { auth: "none" },
    );
  }, []);

  // GET /user/auth/list — NoAuth. Backend filters by auth_id (from invite
  // email URL) or email_domain (derived from EmailToken). Empty result means
  // no custom auth methods configured for this domain — caller should POST
  // /user/auth/select with id:null and the backend uses DEFAULT_USER_AUTH_METHOD.
  const listAuthMethods = useCallback(async ({ authId, emailDomain } = {}) => {
    const params = {};
    if (authId) params.auth_id = authId;
    if (emailDomain) params.email_domain = emailDomain;
    return httpClient.get("/user/auth/list", { auth: "none", params });
  }, []);

  // POST /user/auth/select — SinglePurposeJWTAuth(AuthSelect). `id` is the
  // chosen auth_method id, or null when the email_domain has no methods (then
  // backend falls back to DEFAULT). Response = TokenResponse for the next step.
  const terminateAuthSelect = useCallback(
    async (singlePurposeToken, methodId) => {
      const res = await httpClient.post(
        "/user/auth/select",
        { id: methodId ?? null },
        {
          auth: "single-purpose",
          headers: { Authorization: `Bearer ${singlePurposeToken}` },
        },
      );
      if (res?.token_type === "user_info" && res?.token) {
        await acceptSessionToken(res.token);
      }
      return res;
    },
    [acceptSessionToken],
  );

  // POST /user/accept_invite_from_email runs SinglePurposeJWTAuth on the
  // Bearer header (must be the AcceptInvitationFromEmail SPT) AND separately
  // decodes the body `token` as an EmailToken. The two JWTs are different
  // shapes — see vexgate core/user.rs::accept_invite_from_email_token_only_flow.
  // Caller must preserve the original email-link token from the URL across
  // every intermediate step (auth_select, totp, …) and pass it back here.
  const acceptInviteFromEmail = useCallback(
    async (singlePurposeToken, emailToken) => {
      const res = await httpClient.post(
        "/user/accept_invite_from_email",
        { token: emailToken },
        {
          auth: "single-purpose",
          headers: { Authorization: `Bearer ${singlePurposeToken}` },
        },
      );
      // res is AcceptInviteResponse (untagged): { token, token_type } | "AlreadyAccepted" | "SuccessfullyAccepted"
      if (res && typeof res === "object" && res.token && res.token_type) {
        if (res.token_type === "user_info") {
          await acceptSessionToken(res.token);
        }
      }
      return res;
    },
    [acceptSessionToken],
  );

  // ─── 2FA helpers ───────────────────────────────────────────────────────
  // All call /user/2fa/* with the TOTP-purpose SPT as Bearer. The endpoints
  // themselves use either SinglePurposeJWTAuth(TOTP) (begin/terminate) or
  // SinglePurposeOrLoginTokenAuth(TOTP) (verify/generate/status), so the same
  // SPT works for the whole sub-flow.

  const beginTotp = useCallback(async (singlePurposeToken) => {
    // GET /user/2fa/totp/begin — { secret: { secret, totp_url } | null }.
    // null secret means TOTP is already set on the account; caller switches
    // to the verify-only screen.
    return httpClient.get("/user/2fa/totp/begin", {
      auth: "single-purpose",
      headers: { Authorization: `Bearer ${singlePurposeToken}` },
    });
  }, []);

  const verifyTotp = useCallback(async (singlePurposeToken, code) => {
    // POST /user/2fa/totp/verify — for accounts with TOTP already set.
    // Validates the code against the persisted secret and inserts a token
    // into redis so /terminate can succeed.
    return httpClient.post(
      "/user/2fa/totp/verify",
      { totp: code },
      {
        auth: "single-purpose",
        headers: { Authorization: `Bearer ${singlePurposeToken}` },
      },
    );
  }, []);

  const commitTotp = useCallback(async (singlePurposeToken, code) => {
    // PUT /user/2fa/totp/verify — first-time setup. Validates the code
    // against the redis-buffered secret from /begin, persists it, and
    // marks redis. Recovery codes still need to be generated before
    // /terminate will accept skip=false.
    return httpClient.put(
      "/user/2fa/totp/verify",
      { totp: code },
      {
        auth: "single-purpose",
        headers: { Authorization: `Bearer ${singlePurposeToken}` },
      },
    );
  }, []);

  const verifyRecoveryCode = useCallback(async (singlePurposeToken, code) => {
    return httpClient.post(
      "/user/2fa/recovery_code/verify",
      { recovery_code: code },
      {
        auth: "single-purpose",
        headers: { Authorization: `Bearer ${singlePurposeToken}` },
      },
    );
  }, []);

  const generateRecoveryCodes = useCallback(async (singlePurposeToken) => {
    // GET /user/2fa/recovery_code/generate — returns { recovery_codes: [...] }
    // and persists them on the user row. Required before /terminate(skip=false)
    // for first-time TOTP setup.
    return httpClient.get("/user/2fa/recovery_code/generate", {
      auth: "single-purpose",
      headers: { Authorization: `Bearer ${singlePurposeToken}` },
    });
  }, []);

  const checkTwoFactorStatus = useCallback(async (singlePurposeToken) => {
    // GET /user/2fa/v2 — { status: { totp, recovery_code } | null, is_skippable }.
    // is_skippable mirrors !state.conf.user.force_two_factor_auth.
    return httpClient.get("/user/2fa/v2", {
      auth: "single-purpose",
      headers: { Authorization: `Bearer ${singlePurposeToken}` },
    });
  }, []);

  const terminateTwoFactor = useCallback(
    async (singlePurposeToken, skip) => {
      // GET /user/2fa/terminate?skip_two_factor_auth=<bool> — emits the next
      // SPT in the flow chain and clears redis attempt counters. With skip=true
      // backend bypasses verification ONLY when force_two_factor_auth=false; in
      // prod-like configs the strict block runs anyway and returns
      // TwoFactorAuthRequired/TwoFactorAuthNotSetup.
      const res = await httpClient.get("/user/2fa/terminate", {
        params: { skip_two_factor_auth: skip ? "true" : "false" },
        auth: "single-purpose",
        headers: { Authorization: `Bearer ${singlePurposeToken}` },
      });
      if (res?.token_type === "user_info" && res?.token) {
        await acceptSessionToken(res.token);
      }
      return res;
    },
    [acceptSessionToken],
  );

  const forceSetPassword = useCallback(async (singlePurposeToken, password) => {
    // POST /user/rotate_password — body { password }, auth = ForceSetPassword single-purpose JWT.
    // Response is empty; backend also clears cookie. User must sign in fresh afterwards.
    return httpClient.post(
      "/user/rotate_password",
      { password },
      {
        auth: "single-purpose",
        headers: { Authorization: `Bearer ${singlePurposeToken}` },
      },
    );
  }, []);

  const signOut = useCallback(async () => {
    try {
      await httpClient.post("/user/signout", undefined);
    } catch {
      // best-effort — we still clear client state below
    }
    clearSessionLocal();
  }, [clearSessionLocal]);

  // No-network variant of signOut. Used after operations that already invalidate
  // the JWT server-side (e.g. POST /user/change_password blacklists the token —
  // see core/user.rs:473). Calling /user/signout would 401 and clutter devtools;
  // signOutLocal skips it and just clears client state. Same final state as
  // signOut on the happy path.
  const signOutLocal = useCallback(() => {
    clearSessionLocal();
  }, [clearSessionLocal]);

  const value = useMemo(
    () => ({
      status,
      sessionToken,
      user,
      parentGroups,
      bootstrapError,
      refetchMe,
      hasParent,
      hasPermission,
      signIn,
      userFromEmail,
      listAuthMethods,
      terminateAuthSelect,
      acceptInviteFromEmail,
      beginTotp,
      verifyTotp,
      commitTotp,
      verifyRecoveryCode,
      generateRecoveryCodes,
      checkTwoFactorStatus,
      terminateTwoFactor,
      forceSetPassword,
      acceptSessionToken,
      signOut,
      signOutLocal,
    }),
    [
      status,
      sessionToken,
      user,
      parentGroups,
      bootstrapError,
      refetchMe,
      hasParent,
      hasPermission,
      signIn,
      userFromEmail,
      listAuthMethods,
      terminateAuthSelect,
      acceptInviteFromEmail,
      beginTotp,
      verifyTotp,
      commitTotp,
      verifyRecoveryCode,
      generateRecoveryCodes,
      checkTwoFactorStatus,
      terminateTwoFactor,
      forceSetPassword,
      acceptSessionToken,
      signOut,
      signOutLocal,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
